/* useTelnyxClient — connects the browser softphone to Telnyx via the
 * @telnyx/webrtc SDK and exposes a tiny, opinionated surface to the
 * Softphone component.
 *
 * Lifecycle:
 *   1. With an agent id from `useAgentIdentity`, GET
 *      /api/agent-desk/webrtc-token (sending X-Agent-Id) — that's what
 *      makes per-agent SIP credentials addressable. Worker returns 404
 *      if the agent row has no `sip_username`, in which case we report
 *      `mode: "simulated"` and the Softphone keeps its dev-only
 *      "Simulate ring" path.
 *   2. With creds, instantiate `TelnyxRTC` and `connect()`. Subscribe to
 *      `telnyx.notification` and `telnyx.error`. The SDK fires a
 *      `callUpdate` notification each time a Call's state changes; we
 *      track the active Call and republish a normalised state.
 *   3. On unmount, `disconnect()` and detach listeners.
 *
 * Strict-mode dev double-invocation: React 19 mounts → unmounts →
 * mounts the effect synchronously. The first init() is async, so the
 * cleanup runs while the SDK is still connecting. We track the
 * inflight TelnyxRTC instance in `pendingClient` so the cleanup can
 * tear down the orphaned connection — otherwise we'd leak two
 * simultaneous Verto sessions registered against the same SIP
 * credential, which is exactly what was making inbound calls drop
 * after one ring (Telnyx delivered the INVITE to a stale session).
 *
 * The hook hides the @telnyx/webrtc API from the rest of the app so we
 * can swap providers later without touching components. */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  TelnyxRTC,
  type Call,
  type INotification,
  type ITelnyxWarningEvent,
} from "@telnyx/webrtc";

import { API_URL } from "../lib/api";

// SDK code emitted ~120s before the JWT expires (Telnyx constant
// TOKEN_EXPIRY_WARNING_SECONDS=120). We use it as the trigger to swap
// in a fresh token via client.login({creds}) before REGISTER renewal
// fails silently — the previous failure mode where an agent stayed at
// "Available" with an expired Verto session, missing every INVITE.
const SDK_WARNING_TOKEN_EXPIRING_SOON = 34001;

export type SoftphoneMode =
  | "loading"
  | "live"
  | "simulated"
  | "error"
  // The agent has agent-desk open in another tab/window. Two
  // simultaneous Verto sessions registered against the same
  // telephony_credential makes Telnyx's SIP→Verto bridge round-robin
  // INVITE delivery — the call lands on a stale session ~half the time
  // and silently drops. Guarded via a per-agentId BroadcastChannel:
  // the most recently mounted tab wins, all earlier tabs tear down
  // their SDK and display a "use this elsewhere" banner.
  | "duplicate-tab";

export type TelnyxClientState = {
  mode: SoftphoneMode;
  connected: boolean;
  incomingCall: Call | null;
  currentCall: Call | null;
  error: string | null;
  /** Mic mute state for the active call. Resets per call (always
   * unmuted at the start of a new call) so the agent can't accidentally
   * stay muted across pickups. */
  micMuted: boolean;
  accept: () => void;
  decline: () => void;
  hangup: () => void;
  setMicMuted: (muted: boolean) => void;
};

type TokenResponse = {
  // JWT minted server-side from a per-agent telephony_credential. The
  // SDK uses `login_token` rather than user/pass because Telnyx's
  // SIP→Verto bridge only delivers INVITEs to JWT-authenticated
  // Verto sessions (raw-cred sessions register but never ring).
  login_token: string;
  sip_username: string;
  sip_uri: string;
};

export function useTelnyxClient(agentId: number | null): TelnyxClientState {
  const [mode, setMode] = useState<SoftphoneMode>("loading");
  const [connected, setConnected] = useState(false);
  const [incomingCall, setIncomingCall] = useState<Call | null>(null);
  const [currentCall, setCurrentCall] = useState<Call | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [micMuted, setMicMutedState] = useState(false);

  useEffect(() => {
    if (agentId === null) {
      setMode("loading");
      return;
    }

    let cancelled = false;
    let pendingClient: TelnyxRTC | null = null;
    let channel: BroadcastChannel | null = null;
    let refreshInFlight = false;

    const log = (event: string, detail?: unknown) => {
      // eslint-disable-next-line no-console
      console.info(`[telnyx-rtc] ${event}`, detail ?? "");
    };

    function teardownSdk() {
      const c = pendingClient;
      pendingClient = null;
      if (c) void c.disconnect().catch(() => {});
      setConnected(false);
      setIncomingCall(null);
      setCurrentCall(null);
    }

    // Multi-tab guard. Each tab opens a BroadcastChannel scoped to the
    // agent_id and posts a "claim" message. Any other tab listening on
    // the same channel sees the claim and tears down its own SDK
    // (last-tab-wins). This avoids the SIP→Verto race where two
    // registered sessions exist for the same credential and Telnyx
    // delivers the INVITE to whichever it picks — calls drop ~50% of
    // the time, the symptom Ginni and Margherita reported.
    //
    // Refresh-safe: when a tab refreshes, the old context unmounts (and
    // closes its BroadcastChannel) before the new one mounts and posts
    // its claim. So no kick happens against yourself.
    if (typeof BroadcastChannel !== "undefined") {
      channel = new BroadcastChannel(`agent-desk-presence-${agentId}`);
      channel.onmessage = (ev: MessageEvent<{ type?: string }>) => {
        if (cancelled) return;
        if (ev.data?.type === "claim") {
          log("kicked by another tab — going duplicate-tab mode");
          // Flip cancelled so any inflight init() bails before
          // creating a second TelnyxRTC instance (the very thing the
          // multi-tab guard is here to prevent).
          cancelled = true;
          teardownSdk();
          setMode("duplicate-tab");
          setError(null);
        }
      };
      channel.postMessage({ type: "claim", at: Date.now() });
    }

    // Tab-visibility recovery. When the laptop sleeps or the network
    // hiccups, the OS often suspends the WebSocket without delivering
    // a clean close frame to the browser. The tab wakes back up still
    // believing connected=true, but Verto is gone — heartbeat keeps
    // claiming "available" and INVITEs vanish.
    //
    // visibilitychange firing 'visible' is the most reliable "you are
    // back" signal, more so than socket events on resume. We ask the
    // SDK for ground truth (BaseSession.getIsRegistered, public on
    // TelnyxRTC via inheritance) and reconcile our `connected` state
    // with reality. The SDK's auto-reconnect handles the actual
    // re-REGISTER; we just stop lying about presence in the meantime.
    const onVisibility = () => {
      if (cancelled) return;
      if (document.visibilityState !== "visible") return;
      const c = pendingClient;
      if (!c) return;
      void (async () => {
        try {
          const registered = await c.getIsRegistered();
          if (cancelled) return;
          log(`visibility:visible — getIsRegistered() = ${registered}`);
          // Reflect truth in our state. If events were dropped while
          // suspended, this is the catch-up. The heartbeat effect in
          // Softphone.tsx keys off `connected` and will fire an
          // immediate ping when this changes.
          setConnected(registered);
        } catch (e) {
          log("visibility:visible — getIsRegistered() failed", e);
        }
      })();
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }

    // Surface the mic permission prompt at sign-in instead of mid-ring.
    // We request + immediately release a stream so the browser permission
    // chip flips to "allowed"; the SDK's `getUserMedia` later in the call
    // flow becomes a silent re-acquisition. Failure here is non-fatal —
    // the SDK will request again at answer-time and fail gracefully.
    async function primeMicPermission() {
      if (typeof navigator === "undefined" || !navigator.mediaDevices) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        for (const track of stream.getTracks()) track.stop();
        log("mic permission primed");
      } catch (e) {
        log("mic permission prime failed (will retry on answer)", e);
      }
    }

    async function init() {
      await primeMicPermission();
      let creds: TokenResponse;
      try {
        const r = await fetch(`${API_URL}/api/agent-desk/webrtc-token`, {
          headers: { "X-Agent-Id": String(agentId) },
        });
        if (r.status === 404) {
          // Agent row has no sip_username — the deployment is configured
          // for simulated mode (e.g. an agent provisioned for audit only,
          // or local dev without Telnyx). Fall back silently.
          if (!cancelled) {
            setMode("simulated");
            setError(
              "agent has no SIP credential — softphone runs in simulated mode",
            );
          }
          return;
        }
        if (!r.ok) {
          throw new Error(`webrtc-token ${r.status}`);
        }
        creds = (await r.json()) as TokenResponse;
      } catch (e) {
        if (!cancelled) {
          setMode("error");
          setError(e instanceof Error ? e.message : String(e));
        }
        return;
      }

      if (cancelled) return;

      try {
        const client = new TelnyxRTC({
          login_token: creds.login_token,
          // Default `false`. Explicit so future readers don't need to
          // dig — verbose socket logs are how we triaged the
          // double-register bug that was dropping calls after 1 ring.
          debug: true,
          debugOutput: "socket",
        });
        // Bind the page's `<audio>` element so inbound audio actually
        // plays. Without this, the SDK negotiates the call, accepts
        // SRTP, and dumps the remote MediaStream into the void —
        // perfect "answer" with no sound. The element id has to exist
        // in the DOM by the time a call lands; AgentDesk renders it
        // unconditionally next to the Softphone.
        client.remoteElement = "agent-desk-remote-audio";
        pendingClient = client;

        client.on("telnyx.socket.open", () => {
          if (cancelled) return;
          log("socket.open");
        });

        client.on("telnyx.ready", () => {
          if (cancelled) return;
          log("ready (REGISTER succeeded — softphone is live)");
          setConnected(true);
          setMode("live");
          setError(null);
        });

        client.on("telnyx.error", (event) => {
          if (cancelled) return;
          log("error", event);
          // Don't tear the SDK down on a transient error — log it and
          // surface the message; the SDK has its own reconnect loop.
          const message =
            (event as { error?: { message?: string } })?.error?.message ??
            String(event);
          setError(message);
        });

        client.on("telnyx.socket.error", (event: unknown) => {
          if (cancelled) return;
          log("socket.error", event);
          // Flip connected too — a socket error means we're not reliably
          // reachable until the SDK's reconnect succeeds (which will
          // fire telnyx.ready again). Without this, heartbeat keeps
          // claiming "available" while INVITEs land on a dying session.
          setConnected(false);
          setError(`socket error: ${String(event)}`);
        });

        client.on("telnyx.socket.close", () => {
          if (cancelled) return;
          log("socket.close");
          setConnected(false);
        });

        // Token-expiry warning. Telnyx fires this ~120s before the JWT
        // would otherwise lapse; we mint a fresh one and swap it in via
        // client.login({creds}) so REGISTER renewal stays unbroken. Past
        // failure mode without this: 5-min JWT expires mid-session, the
        // SDK's REGISTER renew silently fails, agent shows "available"
        // forever, every inbound call drops to AI fallback. Hard refresh
        // was the only fix.
        client.on("telnyx.warning", (event: ITelnyxWarningEvent) => {
          if (cancelled) return;
          const { warning } = event;
          log(`warning:${warning.code} ${warning.name}`);
          if (warning.code !== SDK_WARNING_TOKEN_EXPIRING_SOON) return;
          if (refreshInFlight) {
            log("token-refresh already in flight — ignoring duplicate warning");
            return;
          }
          refreshInFlight = true;
          void (async () => {
            try {
              const r = await fetch(`${API_URL}/api/agent-desk/webrtc-token`, {
                headers: { "X-Agent-Id": String(agentId) },
              });
              if (!r.ok) throw new Error(`webrtc-token ${r.status}`);
              const fresh = (await r.json()) as TokenResponse;
              if (cancelled) return;
              await client.login({ creds: { login_token: fresh.login_token } });
              log("token refreshed via login({creds}) — REGISTER cycle continues");
            } catch (e) {
              if (cancelled) return;
              log("token refresh failed", e);
              setError(
                `token refresh failed: ${e instanceof Error ? e.message : String(e)}`,
              );
            } finally {
              refreshInFlight = false;
            }
          })();
        });

        client.on("telnyx.notification", (notification: INotification) => {
          if (cancelled) return;
          // Catch-all log: we want to *see* every notification when
          // diagnosing why a call dropped. Noisy in steady state; worth
          // it during the agent-desk hardening phase.
          log(`notification:${notification.type}`, {
            callId: notification.call?.id,
            state: notification.call?.state,
          });
          if (notification.type !== "callUpdate" || !notification.call) return;
          const call = notification.call;
          switch (call.state) {
            case "ringing":
              setIncomingCall(call);
              break;
            case "active": {
              setIncomingCall((cur) => (cur?.id === call.id ? null : cur));
              setCurrentCall(call);
              // Reset mic mute state on every new call — never carry a
              // stale "muted" UI flag across pickups.
              setMicMutedState(false);
              // Force audio playback. The <audio autoPlay> attribute by
              // itself doesn't reliably play a `srcObject` MediaStream
              // attached after page load — Chrome reports `audioLevel: 0,
              // totalSamplesReceived: 0, packetsDiscarded` rising while
              // packets arrive normally. Calling .play() inside the
              // notification handler runs in the same task as the
              // user's Accept click, so autoplay policy treats it as
              // user-gesture-initiated and lets the stream through.
              const audioEl = document.getElementById(
                "agent-desk-remote-audio",
              ) as HTMLAudioElement | null;
              if (audioEl) {
                audioEl.play().catch((e) => log("audio play() rejected", e));
              } else {
                log("audio element missing — remote stream will be silent");
              }
              break;
            }
            case "hangup":
            case "destroy":
            case "purge":
              setIncomingCall((cur) => (cur?.id === call.id ? null : cur));
              setCurrentCall((cur) => (cur?.id === call.id ? null : cur));
              break;
            default:
              break;
          }
        });

        await client.connect();
      } catch (e) {
        if (!cancelled) {
          setMode("error");
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    }

    void init();

    return () => {
      cancelled = true;
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
      // Close the BroadcastChannel before SDK teardown so a fast
      // refresh doesn't see our channel as still-listening when the
      // new tab posts its claim — that'd cause us to "kick ourselves".
      if (channel) {
        channel.close();
        channel = null;
      }
      teardownSdk();
    };
  }, [agentId]);

  const accept = useCallback(() => {
    incomingCall?.answer();
  }, [incomingCall]);

  const decline = useCallback(() => {
    incomingCall?.hangup();
  }, [incomingCall]);

  const hangup = useCallback(() => {
    currentCall?.hangup();
  }, [currentCall]);

  // Mic mute is delegated to the SDK's Call object — it disables the
  // outbound RTP track at the WebRTC layer so Telnyx (and therefore
  // the customer) literally hears nothing. Local-only "set audio
  // track .enabled = false" would also work but the SDK's helpers
  // are the canonical path and play nicely with hold/transfer.
  const setMicMuted = useCallback(
    (muted: boolean) => {
      if (!currentCall) return;
      // The SDK exposes muteAudio / unmuteAudio. Some versions also
      // surface toggleAudioMute; we use the explicit pair so the UI
      // state and SDK state can't drift.
      const callWithMute = currentCall as unknown as {
        muteAudio?: () => void;
        unmuteAudio?: () => void;
      };
      if (muted) callWithMute.muteAudio?.();
      else callWithMute.unmuteAudio?.();
      setMicMutedState(muted);
    },
    [currentCall],
  );

  return {
    mode,
    connected,
    incomingCall,
    currentCall,
    error,
    micMuted,
    accept,
    decline,
    hangup,
    setMicMuted,
  };
}
